import router from "../router";
import {getTenantData} from '@/services/TenantService';
import {Capacitor} from '@capacitor/core';
import {initializePushNotifications} from '@/services/FirebaseService';
import {checkAppVersion} from '@/services/AppService';
import {Browser} from '@capacitor/browser';
import {App} from '@capacitor/app';
import {getAppDomain, getBiometricLSKeyByDomain, setButtonContentDefault, setButtonContentSuccess, showErrorMessage, showSuccessMessage, startAppLoader} from '@/helper';
import AmlQuestionnaireService from '@/services/AmlQuestionnaireService';
import IdentificationService from '@/services/IdentificationService';
import userService from '@/services/UserService';

let _tokenExpiryInterval = null;

export const authData = {
    loggedUser: null,
};

// Klíče v sessionStorage pro impersonaci. sessionStorage je (na rozdíl od localStorage)
// vázaný jen na konkrétní panel/tab, takže impersonace spuštěná v jednom panelu
// nepřepíše ani neovlivní přihlášení back-office uživatele v jiných otevřených panelech
// (dřív se ACCESS_TOKEN ukládal do sdíleného localStorage a docházelo tak k tomu,
// že akce provedená v jednom panelu byla backendem přiřazena k identitě aktivní
// v jiném panelu - viz IMA reklamace na chybného autora komentáře).
const IMPERSONATION_TOKEN_KEY = 'ACCESS_TOKEN_IMPERSONATE';
const IMPERSONATION_EXPIRY_KEY = 'EXPIRY_IMPERSONATE';

/** True, pokud tento konkrétní panel právě impersonuje jiného uživatele. */
export function isImpersonating() {
    return !!sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
}

/**
 * Vrátí access token platný pro aktuální panel.
 * Pokud panel impersonuje, má přednost token ze sessionStorage,
 * jinak se použije běžný token z localStorage (sdílený mezi panely, jak bylo vždy).
 */
export function getToken() {
    return sessionStorage.getItem(IMPERSONATION_TOKEN_KEY) || localStorage.getItem('ACCESS_TOKEN');
}

/** Vrátí expiraci tokenu ze stejného úložiště, ze kterého vzešel getToken(). */
export function getExpiry() {
    return isImpersonating() ? sessionStorage.getItem(IMPERSONATION_EXPIRY_KEY) : localStorage.getItem('EXPIRY');
}

/**
 * Uloží access token do správného úložiště.
 * @param {string} token
 * @param {{impersonate?: boolean, expiry?: number}} [options]
 *   impersonate: true => token patří jen tomuto panelu (sessionStorage) a nesmí
 *   se zapisovat do sdíleného localStorage, aby neovlivnil ostatní panely.
 *   expiry: unix timestamp konce platnosti; pokud není zadán, dopočítá se z TTL.
 */
export function setToken(token, {impersonate = false, expiry} = {}) {
    const resolvedExpiry = expiry ?? Math.floor(Date.now() / 1000) + (process.env.VUE_APP_JWT_TTL * 60);
    if (impersonate) {
        sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, token);
        sessionStorage.setItem(IMPERSONATION_EXPIRY_KEY, resolvedExpiry);
    } else {
        localStorage.setItem('ACCESS_TOKEN', token);
        localStorage.setItem('EXPIRY', resolvedExpiry);
    }
}

/**
 * Ukončí impersonaci v tomto panelu. Nezapisuje nic do localStorage -
 * identita, kterou měl panel před impersonací, tam po celou dobu zůstala
 * nedotčená, takže po smazání session overlaye se panel k ní automaticky vrátí.
 */
export function clearImpersonation() {
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    sessionStorage.removeItem(IMPERSONATION_EXPIRY_KEY);
}

export function initialize() {
    // set default theme
    void getTenantData(true);

    if (localStorage.getItem('ACCESS_TOKEN')) {
        startAppLoader();
    }

    const appInfo = [
        {plugin: 'PushNotifications', available: Capacitor.isPluginAvailable('PushNotifications')},
        {plugin: 'FirebaseMessaging', available: Capacitor.isPluginAvailable('FirebaseMessaging')},
        {plugin: 'AparajitaCapacitorBiometricAuth', available: Capacitor.isPluginAvailable('AparajitaCapacitorBiometricAuth')},
        {plugin: 'App', available: Capacitor.isPluginAvailable('App')},
        {info: 'isNativePlatform', value: Capacitor.isNativePlatform()},
        {info: 'platform', value: Capacitor.getPlatform()},
        {info: 'env', value: process.env},
    ];

    checkAppVersion();

    checkForSocialLogin();

    if (!window.location.href.includes('token=')) {
        getLoggedUser(true).then(user => {
            authData.loggedUser = user;
            if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging') {
                initializePushNotifications();
            }
            if (Capacitor.isNativePlatform()) {
                localStorage.setItem(getBiometricLSKeyByDomain('BIOMETRIC_UUID'), user.biometric_uuid);
            }
        }, (error) => {
            if (error.status === 401) {
                if (!window.location.href.includes('login') && !window.location.href.includes('activation') && !window.location.href.includes('forgotten-password') && !window.location.href.includes('reset-password')) {
                    sessionStorage.setItem('REDIRECT_AFTER_LOGIN', window.location.href);
                }
            }
            authData.loggedUser = null;
        });
    }
}

export function forgottenPassword(email) {
    return new Promise((resolve, reject) => {
        http.post('api/auth/forgotten-password', {email: email}).then(response => {
            resolve(response.data);
        }).catch((error) => {
            reject(error.response.data);
        });
    });
}

export function resetPassword(form) {
    return new Promise((resolve, reject) => {
        http.post('api/auth/reset-password', form).then(response => {
            resolve(response.data);
        }).catch((error) => {
            reject(error.response.data);
        });
    });
}

export function login(email, password, biometricUUID, twoFactorCode) {
    const body = {}
    if (email) {
        body.email = email
    }
    if (password) {
        body.password = password
    }
    if (biometricUUID) {
        body.biometric_uuid = biometricUUID
    }
    if (twoFactorCode) {
        body.verification_code = twoFactorCode
    }
    return new Promise((resolve, reject) => {
        http.post('api/auth/login', body).then(response => {
            if (response.data.access_token) {
                localStorage.setItem('ACCESS_TOKEN', response.data.access_token);
                localStorage.setItem('EXPIRY', Math.floor(Date.now() / 1000) + (process.env.VUE_APP_JWT_TTL * 60));
                authData.loggedUser = response.data.user;
                initialize();
                if (sessionStorage.getItem('REDIRECT_AFTER_LOGIN')) {
                    const redirect = sessionStorage.getItem('REDIRECT_AFTER_LOGIN');
                    sessionStorage.removeItem('REDIRECT_AFTER_LOGIN');
                    window.location.href = redirect;
                }
            }
            resolve(response.data);
        }).catch((error) => {
            reject(error.response.data);
        });
    });
}

export async function forceLogin(token, userId) {
    if (token) {
        // uložit token a expiraci
        localStorage.setItem('ACCESS_TOKEN', token)
        localStorage.setItem(
            'EXPIRY',
            Math.floor(Date.now() / 1000) + (import.meta.env.VITE_JWT_TTL * 60)
        )

        // načíst data uživatele z API (pokud není poslán celý objekt)
        try {
            const {data} = await http.get(`/api/users/${userId}`)
            authData.loggedUser = data
            initialize()
        } catch (e) {
            console.error('Nepodařilo se načíst uživatele', e)
        }

        // přesměruj dál
        if (sessionStorage.getItem('REDIRECT_AFTER_LOGIN')) {
            const redirect = sessionStorage.getItem('REDIRECT_AFTER_LOGIN')
            sessionStorage.removeItem('REDIRECT_AFTER_LOGIN')
            window.location.href = redirect
        } else {
            window.location.href = '/'
        }
    } else {
        console.error('Chybí token')
    }
}

export async function activate(form) {
    return new Promise((resolve, reject) => {
        http.post('api/auth/activate', form).then(response => {
            if (response.data.access_token) {
                localStorage.setItem('ACCESS_TOKEN', response.data.access_token);
                localStorage.setItem('EXPIRY', Math.floor(Date.now() / 1000) + (process.env.VUE_APP_JWT_TTL * 60));
                authData.loggedUser = response.data.user;
                initialize();
            }
            resolve(response.data);
        }).catch((error) => {
            reject(error.response?.data);
        });
    });
}

export async function generate2faCode(form, apiUrl = 'api/auth/activation-code') {
    return new Promise((resolve, reject) => {
        http.post(apiUrl, form).then(response => {
            resolve(response.data);
        }).catch((error) => {
            reject(error.response?.data);
        });
    });
}

export function logout() {
    clearAuthData();
    return new Promise((resolve, reject) => {
        http.post('api/auth/logout').then(() => {
            let url = '/login';
            if (Capacitor.isNativePlatform()) {
                url += '?biometric=false';
            }
            void router.push(url);
            resolve();
        }, error => {
            reject(error);
        });
    });
}

export async function getLoggedUser(init = false) {
    return new Promise(function (resolve, reject) {
        if (authData.loggedUser) {
            resolve(authData.loggedUser);
        } else if (!init) {
            const interval = setInterval(() => {
                if (authData.loggedUser) {
                    clearInterval(interval);
                    resolve(authData.loggedUser);
                }
            }, 100);
        } else {
            http.get('api/auth/profile')
                .then(response => {
                    authData.loggedUser = response.data;
                    resolve(response.data);
                }, error => {
                    reject(error);
                });
        }
    })
}

export async function refreshLoggedUser() {
    authData.loggedUser = null;
    return getLoggedUser(true);
}

export async function getBankIdUrl(scope = 'login') {
    return new Promise((resolve, reject) => {
        http.get('api/auth/bank-id/url', {params: {scope}}).then(response => {
            resolve(response.data);
        }, error => {
            reject(error);
        });
    });
}

export function checkForSocialLogin() {
    // check if there is an error from socal login in the url params
    const url = new URL(window.location.href);
    const error = url.searchParams.get('error');
    if (error) {
        // has to be delayed, otherwise the error message is not shown
        setTimeout(() => {
            router.push('/login');
            showErrorMessage(error);
        }, 500);
    }

    // if it is a mobile app, listen for the opening event of the app with deep link
    if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('App')) {
        App.addListener('appUrlOpen', (data) => {
            // google login in capacitor
            if (data.url.includes('capacitor=true')) {
                const decodedUrl = decodeURI(data.url.replace('inequity:///#', 'login/').replace('&capacitor=true', ''));
                const queryString = decodedUrl.split('?')[1];
                const params = new URLSearchParams(queryString);
                const token = params.get('token');

                // close the capacitor browser
                if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Browser')) {
                    void Browser.close();
                }

                // redirect to home page to trigger the login
                localStorage.setItem('ACCESS_TOKEN', token);
                localStorage.setItem('EXPIRY', Math.floor(Date.now() / 1000) + (process.env.VUE_APP_JWT_TTL * 60));
                window.location.href = '/home';
            }
        });
    }
}

export function checkForBankIdLogin() {
    const stateID = localStorage.getItem('PROVIDER_STATE_ID');
    const identificationID = localStorage.getItem('IDENTIFICATION_ID');
    const bankIDIdentificationCode = localStorage.getItem('BANKID_IDENTIFICATION_CODE');
    const onboardingLastStep = localStorage.getItem('ONBOARDING_LAST_STEP');
    const urlParams = new URLSearchParams(window.location.search);
    const state = urlParams.get('state');
    const code = urlParams.get('code');

    // if the state and code are present in the url, but not in  LS, it means that the user has been redirected from the bankid login in capacitor browser window,
    // so we need to redirect to the correct page with deeplink
    if (state && code && stateID === null && identificationID === null && bankIDIdentificationCode === null) {
        const currentUrl = new URL(window.location.href);
        const params = currentUrl.search;

        window.location.href = `InEquity://login${params}`;
        return;
    }

    // if it is a mobile app, listen for the opening event of the app with deep link
    if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('App')) {
        App.addListener('appUrlOpen', (data) => {
            const stateID = localStorage.getItem('PROVIDER_STATE_ID'); // have to get the values again, because the values are not set yet
            const identificationID = localStorage.getItem('IDENTIFICATION_ID');
            const bankIDIdentificationCode = localStorage.getItem('BANKID_IDENTIFICATION_CODE');
            const onboardingLastStep = localStorage.getItem('ONBOARDING_LAST_STEP');
            const url = new URL(data.url);
            const bankIdCode = url.searchParams.get('code');
            const bankIdState = url.searchParams.get('state');

            // close the capacitor browser
            if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Browser')) {
                void Browser.close();
            }

            // login
            if (stateID && bankIdState && bankIdCode) {
                void sendBankIdCode(bankIdCode);
            }

            // new onboarding identifications
            if (code && bankIDIdentificationCode === 'null' && onboardingLastStep !== null) {
                localStorage.setItem('BANKID_IDENTIFICATION_CODE', code);
                void router.push('/home');
            }

            // old onboarding identifications
            if (identificationID && bankIdState && bankIdCode) {
                getLoggedUser().then(user => {
                    if (user.client.id) {
                        void IdentificationService.saveWithBankID(user.client.id, identificationID, bankIdCode);
                    }
                })
            }
        });

        return;
    }

    // login
    if (stateID === state && code) {
        void sendBankIdCode(code);
    }

    // new onboarding identifications
    if (code && bankIDIdentificationCode === 'null' && onboardingLastStep !== null) {
        localStorage.setItem('BANKID_IDENTIFICATION_CODE', code);
        void router.push('/home');
    }

    // old onboarding identifications
    if (identificationID && state && code) {
        getLoggedUser().then(user => {
            if (user.client.id) {
                void IdentificationService.saveWithBankID(user.client.id, identificationID, code);
            }
        })
    }
}

function sendBankIdCode(code) {
    return new Promise((resolve, reject) => {
        http.get('api/auth/bank-id/callback?code=' + code).then(response => {
            setButtonContentSuccess('btn-bankid-login');
            if (response.data.access_token) {
                localStorage.setItem('ACCESS_TOKEN', response.data.access_token);
                localStorage.setItem('EXPIRY', Math.floor(Date.now() / 1000) + (process.env.VUE_APP_JWT_TTL * 60));
                authData.loggedUser = response.data.user;
                initialize();
            }
            if (response.data.message === 'first_login') {
                void router.push('/home?welcome=true');
            } else {
                void router.push('/home');
            }
            localStorage.removeItem('PROVIDER_STATE_ID');
            resolve(response.data);
        }, error => {
            localStorage.removeItem('PROVIDER_STATE_ID');
            setButtonContentDefault('btn-bankid-login');
            if (error.message === 'user_or_client_not_found') {
                showErrorMessage('Uživatel nebyl nalezen');
            }

            reject(error);
        });
    });
}

export function clearAuthData() {
    clearInterval(_tokenExpiryInterval);
    localStorage.removeItem('ACCESS_TOKEN');
    localStorage.removeItem('EXPIRY');
    clearImpersonation(); // ať se panel po odhlášení nevrátí zpět do rozimpersonované identity
    authData.loggedUser = null;
}
